import FVector from '@data/geometry/FVector';
import { GeometryUtils } from '@data/geometry/GeometryUtils';
import { FacadeComponentType, FacadeDirectionMode, FacadeType, FloorBasedFacadeState, VerticalFacadeCoverageMode } from '@visualizer/types';
import Grid from 'Geodata/grid';
import type { SensorData } from 'Simulations/SensorPoint';
import * as THREE from 'three';
import { tagDesignSchemeSurfaceMaterial } from '../../DesignSchemeSurfaceMaterialHelper';
import { computeAverageDirectionFromSensors } from '../SensorDirection';
import { FacadeBuilder, FacadeRing } from './FacadeBuilder';
import { FacadeComponentBuilder } from './FacadeComponents/FacadeComponentBuilder';
import { HorizontalEdgePanelInfo, HorizontallyOrientedFacade } from './FacadeHorizontallyOriented';
import { isEdgeFacing } from './FacadeVisibility';
import { FloorPerimeters, Slab } from './Slab';

type FloorMaterials = {
    floor: THREE.Material;
    edges: THREE.LineBasicMaterial;
};

/**
 * Represents a single floor:
 *  - Computes perimeter from SensorData
 *  - Generates the ground (slab), ceiling, and facades
 */
export class Floor {
    // Core properties:
    private readonly facadeSensors: SensorData[];
    private readonly componentSensorsByType: Partial<Record<FacadeComponentType, SensorData[]>>;
    private readonly facadeConfig: FloorBasedFacadeState;
    private readonly facadeRings: FacadeRing[];
    private readonly facadeDirection: FVector | undefined;
    private readonly componentDirectionByType: Partial<Record<FacadeComponentType, FVector | undefined>>;

    // Geometric properties:
    private readonly perimeters: FloorPerimeters;
    private readonly height: number;
    private readonly thickness: number;
    private readonly elevation: number;

    // Neighbours:
    private readonly previousFloor: Floor | undefined;
    private nextFloor: Floor | undefined;

    // Materials:
    private readonly materials = {
        floor: new THREE.MeshBasicMaterial({
            color: 0x808080,
            side: THREE.DoubleSide,
            polygonOffset: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        }),
        edges: new THREE.LineBasicMaterial({ color: 0x000000 }),
    };

    constructor(
        facadeSensors: SensorData[],
        componentSensorsByType: Partial<Record<FacadeComponentType, SensorData[]>>,
        facadeConfig: FloorBasedFacadeState,
        facadeDirection: FVector | undefined,
        componentDirectionByType: Partial<Record<FacadeComponentType, FVector | undefined>>,
        internalLoops: FVector[][],
        externalLoops: FVector[][] | undefined,
        height: number,
        thickness: number,
        elevation: number,
        previousFloor: Floor | undefined
    ) {
        // Core:
        this.facadeSensors = facadeSensors;
        this.componentSensorsByType = componentSensorsByType;
        this.facadeConfig = facadeConfig;
        this.facadeDirection = this.resolveDirection(
            facadeDirection,
            this.facadeSensors,
            this.facadeConfig.directionMode === FacadeDirectionMode.FLOOR_AVERAGE
        );
        this.componentDirectionByType = Object.fromEntries(
            Object.entries(this.componentSensorsByType).map(([componentType, sensors]) => {
                const componentTypeKey = Number(componentType) as FacadeComponentType;
                const shouldUseFloorAverage = this.facadeConfig.componentLink
                    ? this.facadeConfig.directionMode === FacadeDirectionMode.FLOOR_AVERAGE
                    : this.facadeConfig.componentDirectionMode === FacadeDirectionMode.FLOOR_AVERAGE;
                return [
                    componentTypeKey,
                    this.resolveDirection(componentDirectionByType[componentTypeKey], sensors ?? [], shouldUseFloorAverage),
                ];
            })
        ) as Partial<Record<FacadeComponentType, FVector | undefined>>;

        // Geom:
        this.perimeters = { internalLoops, externalLoops };
        this.height = height;
        this.thickness = thickness;
        this.elevation = elevation;

        // Neighbours:
        this.previousFloor = previousFloor;

        // Materials:
        tagDesignSchemeSurfaceMaterial(this.materials.floor, 'opaque');

        // Initialize facades after geometric fields are set.
        this.facadeRings = this.initializeFacades();
    }

    /**
     * Build the entire floor group: ground + ceiling + facades.
     */
    build(): THREE.Group {
        const floorGroup = new THREE.Group();

        const { ground, ceiling } = this.createSlabs();
        floorGroup.add(ground, ceiling);

        if (!this.facadeConfig.hide) {
            const facades = new THREE.Group();
            for (const ring of this.facadeRings) {
                facades.add(this.buildRingGroup(ring));
            }
            floorGroup.add(facades);
        }

        return floorGroup;
    }

    // =====================================
    // UTILITIES
    // =====================================

    /**
     * Resolves floor-level direction only for FLOOR_AVERAGE.
     * If still undefined, each facade edge will resolve its own direction.
     */
    private resolveDirection(direction: FVector | undefined, sensors: SensorData[], useFloorAverage: boolean): FVector | undefined {
        if (direction) return direction.clone();
        if (useFloorAverage) {
            return computeAverageDirectionFromSensors(sensors);
        }
        return undefined;
    }

    // =====================================
    // SLABS
    // =====================================

    /**
     * Builds the ground and ceiling slabs by delegating the footprint merge to Slab.ts.
     */
    private createSlabs(): { ground: THREE.Group; ceiling: THREE.Group } {
        const withEdges = this.shouldRenderSlabEdges();
        const materials: FloorMaterials = { floor: this.materials.floor, edges: this.materials.edges };
        const previousPerimeters = this.getSharedPreviousFloorPerimeters();
        const nextPerimeters = this.getSharedNextFloorPerimeters();

        const ground = new Slab(
            this.elevation - this.thickness,
            this.thickness,
            this.perimeters,
            previousPerimeters,
            materials,
            withEdges && !previousPerimeters
        ).build();

        const ceiling = new Slab(
            this.elevation + this.height,
            this.thickness,
            this.perimeters,
            nextPerimeters,
            materials,
            withEdges
        ).build();

        return { ground, ceiling };
    }

    /**
     * Horizontal facades usually provide their own readable edge language.
     * Re-enable slab edges only when facades are hidden.
     * Missing-sensor horizontal segments render their own local edge caps.
     */
    private shouldRenderSlabEdges(): boolean {
        if (this.facadeConfig.type !== FacadeType.HORIZONTALLY_ORIENTED) return true;
        return this.facadeConfig.hide;
    }

    /**
     * Reuses the previous floor only when its top slab touches this floor's ground slab.
     */
    private getSharedPreviousFloorPerimeters(): FloorPerimeters | undefined {
        if (!this.previousFloor) return undefined;
        if (!this.isSharedWithPreviousFloor()) return undefined;
        return this.previousFloor.getPerimeters();
    }

    /**
     * Reuses the next floor only when its ground slab touches this floor's ceiling slab.
     */
    private getSharedNextFloorPerimeters(): FloorPerimeters | undefined {
        if (!this.nextFloor) return undefined;
        if (!this.isSharedWithNextFloor()) return undefined;
        return this.nextFloor.getPerimeters();
    }

    /**
     * Detects whether the previous floor directly shares the slab interface below this floor.
     */
    private isSharedWithPreviousFloor(): boolean {
        if (!this.previousFloor) return false;
        return Math.abs(this.previousFloor.getElevation() + this.previousFloor.getHeight() - (this.elevation - this.thickness)) <= 1e-6;
    }

    /**
     * Detects whether the next floor directly shares the slab interface above this floor.
     */
    private isSharedWithNextFloor(): boolean {
        if (!this.nextFloor) return false;
        return Math.abs(this.elevation + this.height - (this.nextFloor.getElevation() - this.nextFloor.getThickness())) <= 1e-6;
    }

    // =====================================
    // FACADES
    // =====================================

    /**
     * Create facades for one perimeter loop of this floor.
     */
    private initializeFacades(): FacadeRing[] {
        return this.perimeters.internalLoops.filter((perimeter) => perimeter.length > 0).map((perimeter) => this.createRing(perimeter));
    }

    /**
     * Computes the indices of the edges to render and Builds the facades for a single perimeter.
     */
    private createRing(perimeter: FVector[]): FacadeRing {
        // 1. Check which component(s) to put in this facades ring:
        const componentLink = this.facadeConfig.componentLink;
        const hasComponent = this.facadeConfig.componentTypes.length > 0 && this.facadeConfig.type !== FacadeType.HORIZONTALLY_ORIENTED;
        const componentContextByType: FacadeRing['componentContextByType'] = {};

        // 2. Initialize the ring-level edge classification for components:
        if (hasComponent) {
            for (const componentType of this.facadeConfig.componentTypes) {
                const facingIndices = this.computeGenericComponentEdgeSelection(perimeter, componentType, componentLink);
                const adjacentIndices = new Set<number>();
                let renderedEdgeIndices: Set<number>;

                if (componentType === FacadeComponentType.PV_PANELS) {
                    renderedEdgeIndices = this.computePvRenderedEdgeIndices(
                        perimeter,
                        componentType,
                        componentLink,
                        facingIndices,
                        adjacentIndices
                    );
                } else if (componentType === FacadeComponentType.BALCONIES && this.facadeConfig.balconyOnlyOnOpenings) {
                    renderedEdgeIndices = this.computeBalconiesOnlyOnOpeningsEdgeIndices(perimeter, facingIndices);
                } else {
                    renderedEdgeIndices = new Set(facingIndices);
                }

                // 2.4 Store per-type ring context for geometry construction phase.
                componentContextByType[componentType] = { facingIndices, adjacentIndices, renderedEdgeIndices };
            }
        }

        // 3. Build facade geometry from facade sensors (if the given direction is undefined up to here):
        const isGroundFloor = !this.previousFloor;
        const facades = perimeter.map((point, index) => {
            const next = (index + 1) % perimeter.length;
            const p1 = new FVector(point.x, point.y, this.elevation);
            const p2 = new FVector(perimeter[next].x, perimeter[next].y, this.elevation);

            return FacadeBuilder.buildFacade({
                sensors: this.getSensorsNearEdge(this.facadeSensors, p1, p2),
                p1,
                p2,
                perimeter,
                facadeConfig: this.facadeConfig,
                height: this.height,
                thickness: this.thickness,
                direction: this.facadeDirection,
                visibilityLoops: this.perimeters.internalLoops,
                isGroundFloor: isGroundFloor,
            });
        });

        // 4. Return facades and per-component edge classification for the component pass.
        return {
            perimeter,
            facades,
            componentContextByType: hasComponent ? componentContextByType : undefined,
        };
    }

    /**
     * Traverses one facade ring and performs the optional horizontal panel seam merge.
     */
    private buildRingGroup(ring: FacadeRing): THREE.Group {
        const group = new THREE.Group();

        let previousTrailingHorizontalPanel: HorizontalEdgePanelInfo | undefined;
        let firstLeadingHorizontalPanel: HorizontalEdgePanelInfo | undefined;
        let previousHorizontalFacadeGroup: THREE.Group | undefined;
        let firstHorizontalFacadeGroup: THREE.Group | undefined;

        const mergeHorizontalCoplanarPanels = (
            previousPanel: HorizontalEdgePanelInfo | undefined,
            nextPanel: HorizontalEdgePanelInfo | undefined,
            previousGroup: THREE.Group | undefined,
            nextGroup: THREE.Group | undefined
        ) => {
            if (!this.facadeConfig.horizontalFuseCoplanar) return;

            const coplanarPoint = HorizontallyOrientedFacade.mergeAdjacentPanels(group, previousPanel, nextPanel);
            if (coplanarPoint && previousGroup && nextGroup) {
                HorizontallyOrientedFacade.removeVerticalCoplanarEdges(previousGroup, coplanarPoint);
                HorizontallyOrientedFacade.removeVerticalCoplanarEdges(nextGroup, coplanarPoint);
            }
        };

        for (let index = 0; index < ring.facades.length; index++) {
            const facade = ring.facades[index];
            const facadeGroup = facade.getGroup();
            if (facadeGroup) group.add(facadeGroup);

            // =====================================
            // COMPONENTS
            // =====================================

            if (
                this.facadeConfig.componentTypes.length > 0 &&
                this.facadeConfig.type !== FacadeType.HORIZONTALLY_ORIENTED &&
                ring.componentContextByType
            ) {
                const next = (index + 1) % ring.perimeter.length;
                const p1 = new FVector(ring.perimeter[index].x, ring.perimeter[index].y, this.elevation);
                const p2 = new FVector(ring.perimeter[next].x, ring.perimeter[next].y, this.elevation);
                const edgeComponentSensorsByType = Object.fromEntries(
                    Object.entries(this.componentSensorsByType).map(([componentType, sensors]) => [
                        Number(componentType) as FacadeComponentType,
                        this.facadeConfig.componentLink
                            ? this.getSensorsNearEdge(sensors ?? [], p1, p2).filter((sensor) => sensor.getScalarValue() > 0)
                            : this.getSensorsNearEdge(sensors ?? [], p1, p2),
                    ])
                ) as Partial<Record<FacadeComponentType, SensorData[]>>;
                const edge = {
                    sensorsByType: edgeComponentSensorsByType,
                    p1,
                    p2,
                    perimeter: ring.perimeter,
                    height: this.height,
                    thickness: this.thickness,
                    directionByType: Object.fromEntries(
                        this.facadeConfig.componentTypes.map((componentType) => [
                            componentType,
                            this.componentDirectionByType[componentType],
                        ])
                    ) as Partial<Record<FacadeComponentType, FVector | undefined>>,
                    isFacingByType: Object.fromEntries(
                        Object.entries(ring.componentContextByType).map(([componentType, ctx]) => [
                            Number(componentType) as FacadeComponentType,
                            ctx?.facingIndices.has(index) ?? false,
                        ])
                    ) as Partial<Record<FacadeComponentType, boolean>>,
                    isAdjacentByType: Object.fromEntries(
                        Object.entries(ring.componentContextByType).map(([componentType, ctx]) => [
                            Number(componentType) as FacadeComponentType,
                            ctx?.adjacentIndices.has(index) ?? false,
                        ])
                    ) as Partial<Record<FacadeComponentType, boolean>>,
                    renderedEdgeIndicesByType: Object.fromEntries(
                        Object.entries(ring.componentContextByType).map(([componentType, ctx]) => [
                            Number(componentType) as FacadeComponentType,
                            ctx?.renderedEdgeIndices ?? new Set<number>(),
                        ])
                    ) as Partial<Record<FacadeComponentType, Set<number>>>,
                    visibilityLoops: this.perimeters.internalLoops,
                };
                FacadeComponentBuilder.buildComponents({ edge, facadeConfig: this.facadeConfig }).forEach((component) => {
                    const componentGroup = component.getGroup();
                    if (componentGroup) group.add(componentGroup);
                });
            }

            if (this.facadeConfig.type === FacadeType.HORIZONTALLY_ORIENTED && facadeGroup) {
                const leadingEdgePanel = HorizontallyOrientedFacade.getLeadingEdgePanel(facadeGroup);
                const trailingEdgePanel = HorizontallyOrientedFacade.getTrailingEdgePanel(facadeGroup);

                if (!firstLeadingHorizontalPanel && leadingEdgePanel) {
                    firstLeadingHorizontalPanel = leadingEdgePanel;
                    firstHorizontalFacadeGroup = facadeGroup;
                }

                mergeHorizontalCoplanarPanels(
                    previousTrailingHorizontalPanel,
                    leadingEdgePanel,
                    previousHorizontalFacadeGroup,
                    facadeGroup
                );

                if (trailingEdgePanel) {
                    previousTrailingHorizontalPanel = trailingEdgePanel;
                    previousHorizontalFacadeGroup = facadeGroup;
                }
            }
        }

        if (this.facadeConfig.type === FacadeType.HORIZONTALLY_ORIENTED) {
            mergeHorizontalCoplanarPanels(
                previousTrailingHorizontalPanel,
                firstLeadingHorizontalPanel,
                previousHorizontalFacadeGroup,
                firstHorizontalFacadeGroup
            );
        }

        return group;
    }

    // =====================================
    // COMPONENTS - EDGE SELECTION
    // =====================================

    // 1) Generic: Computes edge selection for All components:
    private computeGenericComponentEdgeSelection(perimeter: FVector[], componentType: FacadeComponentType, componentLink: boolean) {
        return componentLink
            ? this.computeComponentFacingEdgeIndices(perimeter, componentType, true)
            : this.computeFreeFacadeEdgeIndices(perimeter, componentType);
    }

    private computeComponentFacingEdgeIndices(
        perimeter: FVector[],
        componentType: FacadeComponentType,
        requireActiveSensors: boolean
    ): Set<number> {
        const facingIndices = new Set<number>();
        for (let i = 0; i < perimeter.length; i++) {
            const next = (i + 1) % perimeter.length;
            const p1 = new FVector(perimeter[i].x, perimeter[i].y, this.elevation);
            const p2 = new FVector(perimeter[next].x, perimeter[next].y, this.elevation);
            const edgeSensors = this.getSensorsNearEdge(this.componentSensorsByType[componentType] ?? [], p1, p2);
            if (requireActiveSensors && !Floor.hasActiveSensors(edgeSensors)) continue;

            const edgeDirection = this.componentDirectionByType[componentType] ?? computeAverageDirectionFromSensors(edgeSensors);
            if (!edgeDirection || FVector.isZero(edgeDirection)) continue;

            if (isEdgeFacing(p1, p2, perimeter, edgeDirection, this.perimeters.internalLoops)) {
                facingIndices.add(i);
            }
        }
        return facingIndices;
    }

    private computeFreeFacadeEdgeIndices(perimeter: FVector[], componentType?: FacadeComponentType): Set<number> {
        const mode = this.facadeConfig.componentDirectionMode ?? FacadeDirectionMode.FLOOR_AVERAGE;
        const sensors = componentType !== undefined ? (this.componentSensorsByType[componentType] ?? []) : this.facadeSensors;
        const sharedDirection = componentType !== undefined ? this.componentDirectionByType[componentType] : this.facadeDirection;

        // 1. Computed shared direction only for Floor/Volume average modes:
        const facingIndices = new Set<number>();
        const direction =
            mode === FacadeDirectionMode.FACADE_AVERAGE ? undefined : (sharedDirection ?? computeAverageDirectionFromSensors(sensors));

        // 2. If no shared direction (in Floor/Volume average mode), skip:
        if (mode !== FacadeDirectionMode.FACADE_AVERAGE && (!direction || FVector.isZero(direction))) {
            return facingIndices;
        }

        // 3. Test every perimeter edge against the correct direction source:
        for (let i = 0; i < perimeter.length; i++) {
            const next = (i + 1) % perimeter.length;
            const p1 = new FVector(perimeter[i].x, perimeter[i].y, this.elevation);
            const p2 = new FVector(perimeter[next].x, perimeter[next].y, this.elevation);

            // 3.1. If Facade average mode, compute facade direction:
            const edgeDirection =
                mode === FacadeDirectionMode.FACADE_AVERAGE
                    ? computeAverageDirectionFromSensors(this.getSensorsNearEdge(sensors, p1, p2))
                    : direction;

            // 3.2. Skip facades with no direction:
            if (!edgeDirection || FVector.isZero(edgeDirection)) continue;

            // 3.3. Keep the facades if it is not obstructed in the selected direction:
            if (isEdgeFacing(p1, p2, perimeter, edgeDirection, this.perimeters.internalLoops)) {
                facingIndices.add(i);
            }
        }

        // 4. Return the final set of facade-facing edges:
        return facingIndices;
    }

    // 2) Balconies: Computes edge selection for balconies with "only on Openings" option:
    private computeBalconiesOnlyOnOpeningsEdgeIndices(perimeter: FVector[], facingIndices: Set<number>): Set<number> {
        const renderedEdgeIndices = new Set<number>();
        facingIndices.forEach((idx) => {
            const next = (idx + 1) % perimeter.length;
            const p1 = new FVector(perimeter[idx].x, perimeter[idx].y, this.elevation);
            const p2 = new FVector(perimeter[next].x, perimeter[next].y, this.elevation);
            if (this.isTransparentBalconySeedEdge(p1, p2, perimeter)) renderedEdgeIndices.add(idx);
        });
        return renderedEdgeIndices;
    }

    private isTransparentBalconySeedEdge(p1: FVector, p2: FVector, perimeter: FVector[]): boolean {
        if (this.facadeConfig.type === FacadeType.TRANSPARENT) {
            return true;
        }

        const edgeFacadeSensors = this.getSensorsNearEdge(this.facadeSensors, p1, p2);

        if (this.facadeConfig.type === FacadeType.TRANSPARENT_MAIN_DIRECTION) {
            const edgeDirection = this.facadeDirection ?? computeAverageDirectionFromSensors(edgeFacadeSensors);
            if (!edgeDirection || FVector.isZero(edgeDirection)) return false;
            return isEdgeFacing(p1, p2, perimeter, edgeDirection, this.perimeters.internalLoops);
        }

        if (this.facadeConfig.type === FacadeType.HORIZONTALLY_ORIENTED || this.facadeConfig.type === FacadeType.FACADE_WITH_OPENINGS) {
            return Floor.hasActiveSensors(edgeFacadeSensors);
        }

        return false;
    }

    // 3) PV Panels: Computes edges selection for Pv Panels with "adjacent/all" option:
    private computePvRenderedEdgeIndices(
        perimeter: FVector[],
        componentType: FacadeComponentType,
        componentLink: boolean,
        facingIndices: Set<number>,
        adjacentIndices: Set<number>
    ): Set<number> {
        const renderedEdgeIndices = new Set<number>();
        const coverageMode = this.facadeConfig.verticalCoverageMode;
        if (coverageMode === VerticalFacadeCoverageMode.ALL) {
            const eligibleIndices = this.computeComponentEligibleEdgeIndices(perimeter, componentType, componentLink);
            eligibleIndices.forEach((idx) => renderedEdgeIndices.add(idx));
            return renderedEdgeIndices;
        }

        this.populateAdjacentIndices(facingIndices, adjacentIndices, perimeter.length);
        facingIndices.forEach((idx) => renderedEdgeIndices.add(idx));
        if (coverageMode === VerticalFacadeCoverageMode.FACING_AND_ADJACENT) {
            adjacentIndices.forEach((idx) => renderedEdgeIndices.add(idx));
        }
        return renderedEdgeIndices;
    }

    private computeComponentEligibleEdgeIndices(
        perimeter: FVector[],
        componentType: FacadeComponentType,
        requireActiveSensors: boolean
    ): Set<number> {
        const eligibleIndices = new Set<number>();
        for (let i = 0; i < perimeter.length; i++) {
            const next = (i + 1) % perimeter.length;
            const p1 = new FVector(perimeter[i].x, perimeter[i].y, this.elevation);
            const p2 = new FVector(perimeter[next].x, perimeter[next].y, this.elevation);
            const edgeSensors = this.getSensorsNearEdge(this.componentSensorsByType[componentType] ?? [], p1, p2);
            if (requireActiveSensors && !Floor.hasActiveSensors(edgeSensors)) continue;

            const edgeDirection = this.componentDirectionByType[componentType] ?? computeAverageDirectionFromSensors(edgeSensors);
            if (!edgeDirection || FVector.isZero(edgeDirection)) continue;
            eligibleIndices.add(i);
        }
        return eligibleIndices;
    }

    private populateAdjacentIndices(facingIndices: Set<number>, adjacentIndices: Set<number>, perimeterLength: number): void {
        for (const idx of facingIndices) {
            adjacentIndices.add((idx - 1 + perimeterLength) % perimeterLength);
            adjacentIndices.add((idx + 1) % perimeterLength);
        }

        facingIndices.forEach((idx) => adjacentIndices.delete(idx));
    }

    // =====================================
    // SENSOR DATA HANDLING
    // =====================================

    private getSensorsNearEdge(sensors: SensorData[], edgeStart: FVector, edgeEnd: FVector): SensorData[] {
        return sensors.filter((sensor) => Floor.isSensorCloseToFacadeEdge(sensor.getPosition(), edgeStart, edgeEnd, Grid.gridRes));
    }

    private static hasActiveSensors(sensors: SensorData[]): boolean {
        return sensors.some((sensor) => sensor.getScalarValue() > 0);
    }

    private static isSensorCloseToFacadeEdge(sensor: FVector, edgeStart: FVector, edgeEnd: FVector, maxDistance: number): boolean {
        return (
            GeometryUtils.distanceToLineSegment(
                new THREE.Vector3(sensor.x, sensor.y, 0),
                new THREE.Vector3(edgeStart.x, edgeStart.y, 0),
                new THREE.Vector3(edgeEnd.x, edgeEnd.y, 0)
            ) <= maxDistance
        );
    }

    // =====================================
    // GETTERS
    // =====================================

    /**
     * Returns the loops of the current floor in the format expected by Slab.ts.
     */
    getPerimeters(): FloorPerimeters {
        return this.perimeters;
    }

    /**
     * Exposes the internal perimeter loops for facade and debugging logic.
     */
    getFacadeRings() {
        return this.facadeRings;
    }

    /**
     * Returns the floor base elevation.
     */
    getElevation() {
        return this.elevation;
    }

    /**
     * Returns the clear height of the floor volume.
     */
    getHeight() {
        return this.height;
    }

    /**
     * Returns the slab thickness used above and below the floor.
     */
    getThickness() {
        return this.thickness;
    }

    // =====================================
    // GETTERS
    // =====================================

    /**
     * Links the floor immediately above this one so slab creation can inspect its perimeters.
     */
    setNextFloor(nextFloor?: Floor) {
        this.nextFloor = nextFloor;
    }
}
